import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { RoleBadge } from "@/components/users/role-badge.tsx";
import { User, UserRole } from "@/lib/api/user.ts";
import { getPrimaryRole } from "@/lib/utils/system-roles";
import { ColumnDef } from "@tanstack/react-table";
import { UserNameColumn } from "./user-name-column";

export const columns: ColumnDef<User>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => {
      const profilePhoto = row.original.profile_picture_url;
      const name = row.getValue("name") as string | null;

      const researchConsent =
        row.original.profile?.metadata?.researchConsent?.consent === true;

      return (
        <UserNameColumn
          profilePhoto={profilePhoto ?? null}
          name={name}
          researchConsent={researchConsent}
          redactedFields={row.original.redacted_fields}
          userId={row.original.id}
        />
      );
    },
  },
  {
    accessorKey: "role",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Role" />
    ),
    enableHiding: false,
    enableSorting: false,

    cell: ({ row }) => {
      const primaryRole = getPrimaryRole(row.original.systemRoles);
      return (
        <div className="flex items-center justify-center">
          <RoleBadge role={primaryRole} />
        </div>
      );
    },
  },
  {
    accessorKey: "phone",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Phone" />
    ),
    enableHiding: false,
    enableSorting: false,
    cell: ({ row }) => {
      const phone = row.getValue("phone") as string | null;
      const redacted = row.original.redacted_fields?.includes("phone") ?? false;
      return (
        <div className="text-center">
          {redacted ? (
            <span className="italic text-muted-foreground">[redacted]</span>
          ) : (
            phone
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "group",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Group" />
    ),
    enableHiding: false,
    enableSorting: false,
    cell: ({ row }) => {
      const assignedGroups = row.original.assigned_groups;
      const label = assignedGroups?.map((group) => group.name).join(", ");
      return (
        <div className="text-center capitalize">{label || "No Group"}</div>
      );
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const deactivated = Boolean(row.original.deactivated);

      if (deactivated) {
        return (
          <div className="flex items-center justify-center">
            <Badge variant="outline" className="px-4 py-2">
              Inactive
            </Badge>
          </div>
        );
      }

      return (
        <div className="flex items-center justify-center">
          <Badge
            variant="secondary"
            className="bg-black px-4 py-2 text-white hover:bg-black"
          >
            Active
          </Badge>
        </div>
      );
    },
    enableHiding: false,
    enableSorting: false,
  },
  {
    accessorKey: "profile_completeness",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Profile" />
    ),
    cell: ({ row }) => {
      const user = row.original;

      // Only show profile completeness for mentors and admins
      if (user.role !== UserRole.MENTOR && user.role !== UserRole.ADMIN) {
        return (
          <div className="flex items-center justify-center">
            <span className="text-gray-400">-</span>
          </div>
        );
      }

      const isComplete = user.is_profile_complete;

      if (isComplete) {
        return (
          <div className="flex items-center justify-center">
            <Badge
              variant="secondary"
              className="bg-green-100 px-3 py-1 text-green-800 hover:bg-green-100"
            >
              Complete
            </Badge>
          </div>
        );
      }

      return (
        <div className="flex items-center justify-center">
          <Badge
            variant="outline"
            className="border-orange-300 px-3 py-1 text-orange-700 hover:bg-orange-50"
          >
            Incomplete
          </Badge>
        </div>
      );
    },
    enableHiding: false,
    enableSorting: false,
  },
];
